import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import PrimaryButton from './buttons/PrimaryButton';
import Tooltip from './Tooltip';
import { formatCurrency } from '../../lib/utils/formatters';

/**
 * Section card that renders monthly Pay Over Time terms as selectable chips
 * following the ui-elements multi-decision flow pattern. Selecting a chip
 * expands an inline breakdown and Continue button within the card.
 *
 * Props:
 *   terms            – Immutable List of available monthly ApprovedTerm records
 *   unavailableTerms – Immutable List of unavailable monthly terms (shown greyed out)
 *   selectedTerm     – { term, term_period, payment } from parent state
 *   setSelectedTerm  – parent state setter
 *   onContinue       – called when the user clicks Continue (same as Terms.js `save`)
 *   cart             – shopping cart entity from Redux
 *   promo            – current promo record (drives first-payment-due days copy)
 *   isLoading        – disables the Continue button while routing
 */
export default function PayOverTimeCard({
  terms,
  unavailableTerms,
  selectedTerm,
  setSelectedTerm,
  onContinue,
  cart,
  promo,
  isLoading,
  showRateBadge = true,
}) {
  const activeChip = terms.find(
    (t) =>
      t.get('term_period') === 'monthly' &&
      t.get('term') === selectedTerm?.term &&
      selectedTerm?.term_period === 'monthly'
  );

  const hasSelectableTerms = terms.size > 0;

  const handleCardClick = hasSelectableTerms
    ? () => {
        if (!activeChip) {
          const first = terms.first();
          setSelectedTerm({
            term: first.get('term'),
            term_period: first.get('term_period'),
            payment: first.get('monthly_payment'),
          });
        }
      }
    : undefined;

  const isCardActive = !!activeChip;

  const handleChipClick = (term) => {
    setSelectedTerm({
      term: term.get('term'),
      term_period: term.get('term_period'),
      payment: term.get('monthly_payment'),
    });
  };

  // Top-right badge: monthly rate % for the selected term.
  // `rate` is stored as a percentage value already (e.g. 0.90 means 0.90%/mo).
  const badgeLabel = () => {
    if (!activeChip) return null;
    const rate = parseFloat(activeChip.get('rate'));
    return `${rate.toFixed(2)}% /mo`;
  };

  // First payment for monthly Pay Over Time uses promo net period when available.
  const firstPaymentDueDays = promo ? promo.get('max_net_payment') + 1 : 31;
  const firstPaymentDue = `${firstPaymentDueDays} days after shipment`;

  const termCount = terms.size + (unavailableTerms?.size || 0);
  const gridColumnsClassName =
    termCount === 4
      ? 'grid-cols-2 sm:grid-cols-4'
      : `grid-cols-${Math.max(1, Math.min(termCount, 3))}`;

  return (
    <div
      className={classNames(
        'mb-4 border rounded-2xl p-5 bg-white shadow-sm transition-all',
        isCardActive
          ? 'border-[3px] border-primary-blue'
          : 'border-zinc-300 hover:border-blue-300 cursor-pointer'
      )}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-1">
        <div>
          <h2 className="text-base sm:text-lg font-bold">Pay Over Time</h2>
          <p className="text-xs sm:text-sm text-secondary-gray mt-0.5">
            Split into equal monthly installments.
          </p>
        </div>
        {showRateBadge && isCardActive && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-blue-50 text-primary-blue border-blue-200 whitespace-nowrap ml-3">
            {badgeLabel()}
          </span>
        )}
      </div>
      {isCardActive && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary-gray mt-2 mb-3">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <FontAwesomeIcon
              icon={['fas', 'check']}
              className="text-success text-xs"
            />
            No early-payoff fee
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <FontAwesomeIcon
              icon={['fas', 'check']}
              className="text-success text-xs"
            />
            Builds business credit
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <FontAwesomeIcon
              icon={['fas', 'check']}
              className="text-success text-xs"
            />
            Change term anytime
          </span>
        </div>
      )}

      {/* Term chips */}
      <div className="mb-2 mt-3">
        {isCardActive && (
          <p className="text-xs text-secondary-gray font-medium mb-2">
            Choose your term
          </p>
        )}
        <div className={classNames('grid gap-2', gridColumnsClassName)}>
          {/* Available terms */}
          {terms.map((term, idx) => {
            const isSelected =
              selectedTerm?.term === term.get('term') &&
              selectedTerm?.term_period === 'monthly';

            return (
              <button
                key={term.get('id')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChipClick(term);
                }}
                className={classNames(
                  'border rounded-lg p-3 text-left transition-all',
                  isSelected
                    ? 'border-[2px] border-primary-blue bg-secondary-blue'
                    : 'border-primary-border-gray hover:border-blue-300'
                )}
              >
                <div className="text-sm font-semibold">
                  {term.get('term')}{' '}
                  {term.get('term') === 1 ? 'month' : 'months'}
                </div>
                <div className="text-xs font-semibold mt-0.5">
                  <span
                    className={
                      isSelected ? 'text-primary-blue' : 'text-primary'
                    }
                  >
                    {formatCurrency(term.get('monthly_payment'))}
                  </span>
                  <span className="text-secondary-gray font-normal">/mo</span>
                </div>
              </button>
            );
          })}

          {/* Unavailable terms — shown greyed out, non-interactive */}
          {unavailableTerms?.map((term, idx) => (
            <div
              key={`unavail-${idx}`}
              className="border border-primary-border-gray rounded-lg p-3 opacity-40 cursor-not-allowed"
            >
              <div className="text-sm font-semibold text-secondary-gray">
                {term.get('term')} {term.get('term') === 1 ? 'month' : 'months'}
              </div>
              <div className="text-xs text-tertiary-gray mt-0.5">
                Unavailable
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Inline breakdown — only visible when a chip is selected */}
      {activeChip && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-secondary-gray">Order total</span>
            <span className="font-medium">
              {formatCurrency(cart?.grand_total)}
            </span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-secondary-gray flex items-center gap-1">
              Total interest
              <Tooltip
                tooltipText={`${activeChip.get('term')} months at ${parseFloat(
                  activeChip.get('rate')
                ).toFixed(2)}%/mo`}
                placement="top"
                triggerClass="px-0.5 hover:cursor-pointer hover:text-primary-blue text-xs"
                containerClass="rounded-2xl p-1.5 px bg-gray-900 text-white text-xs w-max max-w-xs"
              >
                <span className="text-xs text-tertiary-gray">ⓘ</span>
              </Tooltip>
            </span>
            <span className="font-medium">
              {formatCurrency(activeChip.get('total_interest'))}
            </span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-secondary-gray">First payment due</span>
            <span className="font-medium">{firstPaymentDue}</span>
          </div>

          <div className="flex items-center justify-between text-sm font-semibold pt-2 border-t border-primary-border-gray">
            <span>
              {activeChip.get('term')}{' '}
              {activeChip.get('term') === 1
                ? 'monthly payment'
                : 'monthly payments'}{' '}
              of:
            </span>
            <span>{formatCurrency(activeChip.get('monthly_payment'))}</span>
          </div>

          <PrimaryButton
            buttonText="Continue"
            buttonColor="bg-primary-blue"
            hoverClass="hover:bg-primary-blue-hover"
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onContinue}
          />
        </div>
      )}
    </div>
  );
}
